/**
 * Agent dispatch for canvas nodes: creates a flow session from a node and
 * posts its opening message, and owns the node highlight the rail drives on
 * hover. Mount under `CanvasLoroProvider` + `CanvasRouteProvider` (e.g. via
 * `CanvasProvider`); reads `canvasId` and Loro nodes from context.
 */

import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import { useEventStream } from "@/providers/EventStreamProvider";
import { EventStreamEvent } from "@/types/events";
import {
  FLOW_SESSION_MODES,
  type FlowSessionMode,
  type ReferencedEntity,
} from "@cohesive/api-client";
import type { Entity } from "@cohesive/ui-components";
import {
  getEntityData,
  getEntityName,
  getEntityType,
} from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvasApi, useCanvasDoc } from "./CanvasContext";
import { useCanvasRoute } from "./CanvasRouteContext";

// =============================================================================
// External event: a flow session was deleted out-of-band (e.g. from the
// CanvasExplorer chats list). Anyone holding state derived from a flow
// session — currently the `chatSession` node placed by quick-chat — listens
// to this and tears down its local copy. Going through a window event keeps
// the explorer pane decoupled from the canvas page's React tree (they don't
// share a provider), and it survives the canvas not yet being mounted (the
// underlying API delete is the source of truth either way).
// =============================================================================

export const CANVAS_FLOW_SESSION_DELETED_EVENT = "canvas:flow-session-deleted";

export function emitCanvasFlowSessionDeleted(flowSessionId: string) {
  window.dispatchEvent(
    new CustomEvent(CANVAS_FLOW_SESSION_DELETED_EVENT, {
      detail: { flowSessionId },
    }),
  );
}

// =============================================================================
// Types
// =============================================================================

export type CanvasProcessAction = "chat" | "execute_action_item";

export interface DispatchProcessParams {
  nodeId: string;
  /** Entity for entity-type canvas nodes. Omit for canvas-native nodes (textNote, section, etc.). */
  entity?: Entity;
  /** Explicit referenced entities. When provided, overrides the entity-derived referenced_entities. Required when entity is omitted. */
  referencedEntities?: ReferencedEntity[];
  action: CanvasProcessAction;
  customPrompt?: string; // For chat action - user's custom message
}

interface CanvasProcessContextType {
  /** Create a flow session for a node and post its opening message. Returns the flow session ID. */
  dispatchProcess: (
    params: DispatchProcessParams,
  ) => Promise<string | undefined>;
  /** Node IDs to highlight on the canvas (e.g. when hovering a session in the rail) */
  highlightedNodeIds: Set<string>;
  /** Set node IDs to highlight, or null to clear */
  setHighlightedNodeIds: (nodeIds: string[] | null) => void;
}

// =============================================================================
// Action Configuration
// =============================================================================

const ACTION_CONFIG: Record<
  CanvasProcessAction,
  {
    mode: FlowSessionMode;
    prompt: string;
  }
> = {
  chat: {
    mode: FLOW_SESSION_MODES.CANVAS_CHAT,
    prompt: "", // Will be overridden by customPrompt
  },
  execute_action_item: {
    mode: FLOW_SESSION_MODES.CANVAS_CHAT,
    prompt: "Execute this action item.",
  },
};

// =============================================================================
// Context
// =============================================================================

const CanvasProcessContext = createContext<CanvasProcessContextType | null>(
  null,
);

/** Throws unless wrapped by `CanvasProcessProvider` (canvas page subtree). */
export function useCanvasProcess() {
  const context = useContext(CanvasProcessContext);
  if (!context) {
    throw new Error(
      "useCanvasProcess must be used within CanvasProcessProvider",
    );
  }
  return context;
}

const PUBLIC_VIEW_PROCESS_VALUE: CanvasProcessContextType = {
  dispatchProcess: async () => undefined,
  highlightedNodeIds: new Set(),
  setHighlightedNodeIds: () => {},
};

/**
 * No-op dispatch for the public canvas-view SPA (no Auth0 / API / flow
 * sessions). Same context shape so EntityNode can mount unchanged.
 */
export function CanvasProcessStubProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <CanvasProcessContext.Provider value={PUBLIC_VIEW_PROCESS_VALUE}>
      {children}
    </CanvasProcessContext.Provider>
  );
}

// =============================================================================
// Provider
// =============================================================================

/** Supplies flow-session dispatch; reads `canvasId` from `CanvasRouteContext` and nodes from `CanvasLoroContext`. */
export function CanvasProcessProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { canvasId: routeCanvasId } = useCanvasRoute();
  const canvasId = routeCanvasId ?? "";
  const canvas = useCanvasApi();
  const { nodes: canvasNodes } = useCanvasDoc();
  const canvasEnvRef = useRef({ canvas, canvasNodes });
  canvasEnvRef.current = { canvas, canvasNodes };

  // Node IDs highlighted when hovering a session in the rail
  const [highlightedNodeIds, setHighlightedNodeIdsState] = useState<
    Set<string>
  >(new Set());

  const setHighlightedNodeIds = useCallback((nodeIds: string[] | null) => {
    setHighlightedNodeIdsState((prev) => {
      if (!nodeIds || nodeIds.length === 0) {
        return prev.size === 0 ? prev : new Set();
      }
      const next = new Set(nodeIds);
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) {
        return prev;
      }
      return next;
    });
  }, []);

  const queryClient = useQueryClient();
  const { mutateAsync: createFlowSessionAsync } = useCreateFlowSession();
  const { mutateAsync: postFlowMessageAsync } = usePostFlowMessage();

  const { registerHandler } = useEventStream();

  // =============================================================================
  // WebSocket: keep the canvas's flow-session list in lockstep
  // =============================================================================
  useEffect(() => {
    const handleEvent = (event: EventStreamEvent) => {
      // Keep the React Query cache for `["canvas-flow-sessions", canvasId]`
      // in lockstep with WebSocket events so list consumers (the rail's chats
      // / agent-tasks lists, the floating chat menu, etc.) reflect
      // server-spawned sessions immediately. Without this, agent tasks
      // generated by another flow only show up after a manual refresh
      // because nothing else invalidates the cache.
      //
      // Scoped to events for *this* canvas (`canvas_id` on the payload)
      // so multi-canvas tab setups don't refetch every list on every
      // event.
      if (
        canvasId &&
        (event.type === "flow_session.created" ||
          event.type === "flow_session.updated") &&
        event.data.flow_session.canvas_id === canvasId
      ) {
        queryClient.invalidateQueries({
          queryKey: ["canvas-flow-sessions", canvasId],
        });
      }
    };

    return registerHandler(handleEvent);
  }, [registerHandler, canvasId, queryClient]);

  // =============================================================================
  // Listen for out-of-band flow-session deletes (e.g. CanvasExplorer chats
  // list) and remove the `chatSession` node anchored to the session.
  // =============================================================================
  useEffect(() => {
    const handler = (e: Event) => {
      const { flowSessionId } = (e as CustomEvent).detail as {
        flowSessionId?: string;
      };
      if (!flowSessionId) return;
      const { canvas: l, canvasNodes: nodes } = canvasEnvRef.current;

      for (const node of nodes) {
        const data = (node.data ?? {}) as { flowSessionId?: string };
        if (
          node.type === "chatSession" &&
          data.flowSessionId === flowSessionId
        ) {
          l.removeNode(node.id);
        }
      }
    };
    window.addEventListener(CANVAS_FLOW_SESSION_DELETED_EVENT, handler);
    return () =>
      window.removeEventListener(CANVAS_FLOW_SESSION_DELETED_EVENT, handler);
  }, []);

  const dispatchProcess = useCallback(
    async (params: DispatchProcessParams): Promise<string | undefined> => {
      const {
        entity,
        referencedEntities: explicitRefs,
        action,
        customPrompt,
      } = params;
      const config = ACTION_CONFIG[action];

      if (!config) {
        console.error(`Unknown action: ${action}`);
        return undefined;
      }

      const entityData = entity ? getEntityData(entity) : null;

      // Build referenced entities: explicit override takes priority, otherwise derive from entity
      const messageReferencedEntities: ReferencedEntity[] =
        explicitRefs ??
        (entity && entityData
          ? [
              {
                entity_id: String(entityData.id),
                entity_type: getEntityType(entity),
                name: getEntityName(entity),
                platform: entity.platform || "cohesive",
              },
            ]
          : []);

      try {
        // Create flow session. No harness: a delegate is spawned by the canvas agent, not by
        // someone at a composer, so there is no choice to carry — it runs on cohesive.
        const flowSession = await createFlowSessionAsync({
          mode: config.mode,
          class: "canvas",
          canvas_id: canvasId,
        });

        await postFlowMessageAsync({
          flowSessionId: flowSession.id,
          id: uuidv4(),
          content: customPrompt || config.prompt,
          referenced_entities: messageReferencedEntities,
        });

        return flowSession.id;
      } catch (error) {
        console.error("Error dispatching process:", error);
        return undefined;
      }
    },
    [canvasId, createFlowSessionAsync, postFlowMessageAsync],
  );

  const value = useMemo<CanvasProcessContextType>(
    () => ({
      dispatchProcess,
      highlightedNodeIds,
      setHighlightedNodeIds,
    }),
    [dispatchProcess, highlightedNodeIds, setHighlightedNodeIds],
  );

  return (
    <CanvasProcessContext.Provider value={value}>
      {children}
    </CanvasProcessContext.Provider>
  );
}
